import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../store";
import { setQuery } from "../../../store/slices/sucursalesSlice.ts";
import ModalContainer from "../../ModalContainer.tsx";
import TableOperationalUrgenc from "./operationalUrgency/TableOperationalUrgency.tsx";
import GeneralAttendanceReport from "./GeneralAttendanceReportModal.tsx";

const INIT_FILTERS = {
  type: "all",
  param: "",
  partnerName: "",
};

const FilterSucursales = ({ rol }: { rol: string }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useSelector((state: RootState) => state.sucursales.query);
  const [timer, setTimer] = React.useState<ReturnType<typeof setTimeout> | null>(null);
  const [filters, setFilters] = React.useState(INIT_FILTERS);
  const [showPills, setShowPills] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [showReport, setShowReport] = React.useState(false);

  const buildQueryString = useCallback((f: typeof INIT_FILTERS) => {
    return Object.keys(f)
      .filter((key) => f[key as keyof typeof f] !== "" && f[key as keyof typeof f] !== "all")
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(String(f[key as keyof typeof f]))}`)
      .join("&");
  }, []);

  useEffect(() => {
    if (!query) {
      setFilters(INIT_FILTERS);
      setShowPills(false);
      return;
    }
    const params = new URLSearchParams(query);
    const type = params.get("type") ?? "all";
    const param = params.get("param") ?? "";
    const partnerName = params.get("partnerName") ?? "";
    setFilters({ type, param, partnerName });
    setShowPills(Boolean(type !== "all" || param || partnerName));
  }, [query]);

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: "param" | "partnerName"
  ) => {
    const newValue = e.target.value;
    setFilters((prev) => ({ ...prev, [key]: newValue }));
    if (newValue === "") setShowPills(false);
    else setShowPills(true);
    if (timer) clearTimeout(timer);
    setTimer(
      setTimeout(() => {
        const updated = { ...filters, [key]: newValue };
        dispatch(setQuery(buildQueryString(updated)));
        setTimer(null);
      }, 500)
    );
  };

  const updateFilters = () => {
    dispatch(setQuery(buildQueryString(filters)));
    setShowPills(true);
  };

  const clearFilters = () => {
    setFilters(INIT_FILTERS);
    dispatch(setQuery(""));
    setShowPills(false);
  };

  const selectedFilters = () => {
    const items: { key: string; keyName: string; valueName: string }[] = [];
    if (filters.type !== "all") {
      items.push({
        key: "type",
        keyName: "Estatus",
        valueName: filters.type === "true" ? "Activa" : "Desactiva",
      });
    }
    if (filters.param) {
      items.push({ key: "param", keyName: "Sucursal", valueName: filters.param });
    }
    if (filters.partnerName) {
      items.push({ key: "partnerName", keyName: "Chilito", valueName: filters.partnerName });
    }
    return items;
  };

  const removeFilter = (key: keyof typeof INIT_FILTERS) => {
    const next = { ...filters, [key]: key === "type" ? "all" : "" };
    setFilters(next);
    dispatch(setQuery(buildQueryString(next)));
    if (buildQueryString(next) === "") setShowPills(false);
  };

  if (rol === "Lider de sucursal") {
    return (
      <div className="d-flex w-100">
        <button
          onClick={() => navigate(`asistencias`)}
          className="btn btn-azul ms-auto mb-3"
          type="button"
        >
          Lista de asistencia
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="input-group mb-2">
        <button
          className="btn btn-white dropdown-toggle"
          type="button"
          data-bs-toggle="dropdown"
          style={{ borderRadius: "999px 0 0 999px" }}
        >
          <span className="d-none d-md-inline">Filtrar</span>
          <span className="d-inline d-md-none">
            <i className="fa fa-filter"></i>
          </span>
          <b className="caret"></b>
        </button>
        <div className="dropdown-menu p-4" onClick={(e) => e.stopPropagation()}>
          <label>Estatus:</label>
          <select
            className="form-select mt-1"
            id="status"
            name="status"
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            value={filters.type}
          >
            <option value="all">Todos</option>
            <option value="true">Activa</option>
            <option value="false">Desactiva</option>
          </select>
          <div className="row mt-3">
            <div className="col">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  clearFilters();
                }}
                className="btn btn-amarillo d-block w-100 btn-lg fs-14px"
              >
                Limpiar
              </button>
            </div>
            <div className="col">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  updateFilters();
                }}
                className="btn btn-azul d-block w-100 btn-lg fs-14px"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>

        <div className="flex-fill position-relative">
          <div className="input-group" style={{ flexWrap: "nowrap" }}>
            <input
              type="text"
              className="form-control bg-light"
              placeholder="Buscar sucursal..."
              onChange={(e) => handleSearchChange(e, "param")}
              value={filters.param}
            />
            <input
              type="text"
              className="form-control px-35px bg-light"
              placeholder="Buscar chilito..."
              onChange={(e) => handleSearchChange(e, "partnerName")}
              value={filters.partnerName}
              style={{ borderRadius: "0 999px 999px 0" }}
            />
            <div className="d-flex align-items-center gap-2 ms-2 flex-wrap">
              <button
                onClick={() => navigate(`birthdates`)}
                className="btn btn-secondary rounded-pill"
                type="button"
              >
                Cumpleaños
              </button>
              <button
                onClick={() => setOpen(true)}
                className="btn btn-danger rounded-pill"
                type="button"
              >
                Urgencia Operativa
              </button>
              <button
                onClick={() => setShowReport(true)}
                className="btn btn-azul rounded-pill"
                type="button"
              >
                Reporte de asistencias
              </button>
              {["Administrador", "Administrador-"].includes(rol) && (
                <button
                  onClick={() => navigate(`nuevo`)}
                  className="btn btn-primary rounded-pill"
                  type="button"
                >
                  Agregar sucursal
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showPills && selectedFilters().length > 0 && (
        <div className="mb-3">
          {selectedFilters().map((filter) => (
            <span
              key={filter.key}
              className="badge bg-primary text-white fs-6 me-2 position-relative pe-4"
              style={{ padding: "0.35em 0.65em" }}
            >
              {filter.keyName}: {filter.valueName}
              <button
                type="button"
                className="btn-close btn-close-white position-absolute top-0 end-0 mt-1 me-1"
                aria-label="Cerrar"
                onClick={() => removeFilter(filter.key as keyof typeof INIT_FILTERS)}
              />
            </span>
          ))}
        </div>
      )}

      <ModalContainer
        open={open}
        closeFn={() => setOpen(false)}
        title="Urgencia operativa"
        children={<TableOperationalUrgenc />}
        size="xl"
      />
      <ModalContainer
        open={showReport}
        closeFn={() => setShowReport(false)}
        title="Reporte de asistencias"
        children={
          <GeneralAttendanceReport closeFn={() => setShowReport(false)} />
        }
      />
    </div>
  );
};

export default FilterSucursales;
